import { useEffect, useState } from "react";
import api, { formatErr } from "@/lib/api";
import { useDialog } from "@/context/DialogProvider";
import { MapPin, Phone, Plus, Trash2, Pencil, X as XIcon } from "lucide-react";
import StepLayout from "./StepLayout";

/**
 * Step 2b — Sites + onsite emergency contacts.
 *
 * Owner adds each physical location (Site) and, for each site, one or
 * more onsite emergency contacts (e.g. the site manager). Contacts use
 * the existing `/emergency/contacts` endpoint with scope="site" and
 * category="onsite" so they show up in the Incident Response screen.
 *
 * We refuse to advance until at least 1 site exists — Vanguard Beacon
 * is multi-site by design and downstream steps depend on it.
 */
export default function Step2bSites({ stepIndex, totalSteps, onAdvance, onBack, onFinishLater }) {
  const { notify } = useDialog();
  const [sites, setSites] = useState([]);
  const [contacts, setContacts] = useState([]); // all onsite contacts across sites
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [draft, setDraft] = useState({ name: "", location: "" });

  const load = async () => {
    // Load sites + contacts INDEPENDENTLY. The emergency module can be
    // globally hidden (returns 403), which previously rejected a combined
    // Promise.all and left the site list stuck empty even though the site
    // was created fine. Never let a contacts failure hide the sites.
    try {
      const { data: s } = await api.get("/sites");
      setSites(s.sites || []);
    } catch (e) { console.debug("step2b sites load failed", e); }
    try {
      // Use the wizard-friendly flag so contacts from EVERY site of this
      // tenant come back in one shot.
      const { data: c } = await api.get("/emergency/contacts", { params: { include_all_sites: true } });
      const list = (c.contacts || []).filter(
        (x) => x.scope === "site" && x.category === "onsite",
      );
      setContacts(list);
    } catch (e) { console.debug("step2b contacts load failed (module may be hidden)", e); }
  };
  useEffect(() => { load(); }, []);

  const addSite = async () => {
    setErr("");
    const name = draft.name.trim();
    if (!name) return setErr("Site name is required.");
    setBusy(true);
    try {
      await api.post("/sites", { name, location: draft.location.trim() });
      setDraft({ name: "", location: "" });
      await load();
    } catch (ex) {
      // Backend errors (duplicate name, trial expired, etc.) get a dialog
      // popup so the owner doesn't miss them.
      await notify({
        title: "Couldn't add site",
        message: formatErr(ex.response?.data?.detail) || "Please try again.",
        variant: "err",
      });
    }
    finally { setBusy(false); }
  };

  const removeSite = async (siteId) => {
    setBusy(true); setErr("");
    try {
      await api.delete(`/sites/${siteId}`);
      await load();
    } catch (ex) {
      await notify({
        title: "Couldn't remove site",
        message: formatErr(ex.response?.data?.detail) || "Please try again.",
        variant: "err",
      });
    }
    finally { setBusy(false); }
  };

  // Inline edit modal for sites. PUT /sites/{id} accepts {name, location, timezone}.
  const [editSiteId, setEditSiteId] = useState(null);
  const [editSiteDraft, setEditSiteDraft] = useState(null);
  const openEditSite = (s) => {
    setEditSiteId(s.id);
    setEditSiteDraft({ name: s.name || "", location: s.location || "", timezone: s.timezone || "UTC" });
  };
  const closeEditSite = () => { setEditSiteId(null); setEditSiteDraft(null); };
  const saveEditSite = async () => {
    if (!editSiteDraft?.name?.trim()) {
      return notify({ title: "Name required", message: "Site name can't be empty.", variant: "err" });
    }
    setBusy(true);
    try {
      await api.put(`/sites/${editSiteId}`, {
        name: editSiteDraft.name.trim(),
        location: editSiteDraft.location.trim(),
        timezone: editSiteDraft.timezone || "UTC",
      });
      closeEditSite();
      await load();
    } catch (ex) {
      await notify({
        title: "Couldn't save site",
        message: formatErr(ex.response?.data?.detail) || "Please try again.",
        variant: "err",
      });
    } finally { setBusy(false); }
  };

  const next = async () => {
    if (sites.length === 0) {
      return setErr("Add at least one site before continuing.");
    }
    await onAdvance({ sites: true });
  };

  return (
    <StepLayout
      stepIndex={stepIndex}
      totalSteps={totalSteps}
      title="Where are your sites?"
      subtitle="Add each location you run — hotels, kitchens, warehouses. Each gets its own dashboard."
      description={
        <div className="space-y-3">
          <p>For every site, we'll also let you save an <strong>emergency contact</strong> — the person to call when something happens on-site.</p>
          <p className="text-xs opacity-70">These are the panic-call people for incidents (e.g. Site Manager, Maintenance Lead). They're <strong>different</strong> from team members who log daily checks — you'll invite those in the next step.</p>
        </div>
      }
      onBack={onBack}
      onNext={next}
      onFinishLater={onFinishLater}
      nextDisabled={sites.length === 0}
      busy={busy}
      errorMsg={err}
    >
      <div className="space-y-5">
        {/* Add site card */}
        <div className="vb-card p-6 space-y-3" data-testid="wiz-add-site-card">
          <div className="flex items-center gap-2">
            <MapPin size={18} style={{ color: "var(--vb-text-2)" }} />
            <h2 className="vb-heading text-xl m-0" style={{ color: "var(--vb-text-2)" }}>Add a site</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-[1fr_1.4fr_auto] gap-3 items-end">
            <div>
              <Label>Site name *</Label>
              <input className="vb-input" value={draft.name}
                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                placeholder="e.g. Hyatt — Orchard Road"
                data-testid="wiz-site-name" />
            </div>
            <div>
              <Label>Address / location</Label>
              <input className="vb-input" value={draft.location}
                onChange={(e) => setDraft({ ...draft, location: e.target.value })}
                placeholder="10 Scotts Road, Singapore 228211"
                data-testid="wiz-site-location" />
            </div>
            <button type="button" className="vb-btn" onClick={addSite} disabled={busy}
              data-testid="wiz-site-add-btn">
              <Plus size={14} /> Add
            </button>
          </div>
        </div>

        {/* Existing sites + their onsite contacts */}
        {sites.length === 0 ? (
          <div className="vb-card p-6 text-center text-sm opacity-70" data-testid="wiz-sites-empty">
            No sites yet — add your first one above.
          </div>
        ) : (
          <div className="space-y-3" data-testid="wiz-sites-list">
            {sites.map((s) => (
              <SiteRow key={s.id} site={s}
                contacts={contacts.filter((c) => c.site_id === s.id)}
                onReload={load}
                onEdit={() => openEditSite(s)}
                onDelete={() => removeSite(s.id)}
                disabled={busy} />
            ))}
          </div>
        )}
      </div>
      {editSiteId && editSiteDraft && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: "rgba(60, 40, 24, 0.6)" }}
          data-testid="wiz-site-edit-modal">
          <div className="vb-card max-w-md w-full p-5">
            <div className="flex items-center justify-between mb-3">
              <h2 className="vb-heading text-lg m-0" style={{ color: "var(--vb-text-2)" }}>Edit site</h2>
              <button onClick={closeEditSite} className="opacity-60 hover:opacity-100"
                data-testid="wiz-site-edit-close"><XIcon size={18} /></button>
            </div>
            <div className="grid gap-3">
              <div>
                <Label>Site name *</Label>
                <input className="vb-input" value={editSiteDraft.name}
                  onChange={(e) => setEditSiteDraft({ ...editSiteDraft, name: e.target.value })}
                  data-testid="wiz-site-edit-name" />
              </div>
              <div>
                <Label>Address / location</Label>
                <input className="vb-input" value={editSiteDraft.location}
                  onChange={(e) => setEditSiteDraft({ ...editSiteDraft, location: e.target.value })}
                  data-testid="wiz-site-edit-location" />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button type="button" className="vb-btn ghost" onClick={closeEditSite} disabled={busy}
                data-testid="wiz-site-edit-cancel">Cancel</button>
              <button type="button" className="vb-btn" onClick={saveEditSite} disabled={busy}
                data-testid="wiz-site-edit-save">Save</button>
            </div>
          </div>
        </div>
      )}
    </StepLayout>
  );
}

function SiteRow({ site, contacts, onReload, onEdit, onDelete, disabled }) {
  const { notify } = useDialog();
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState({ contact_person: "", contact_number: "", department: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  // Per-contact inline edit state — keyed by contact id.
  const [editContactId, setEditContactId] = useState(null);
  const [editContactDraft, setEditContactDraft] = useState(null);

  const addContact = async () => {
    setErr("");
    if (!draft.contact_number.trim()) return setErr("Phone number is required.");
    setBusy(true);
    try {
      await api.post("/emergency/contacts", {
        scope: "site",
        site_id: site.id,
        category: "onsite",
        description_en: draft.department.trim() || "Emergency contact",
        description_zh: "",
        contact_person: draft.contact_person.trim(),
        department: draft.department.trim(),
        contact_number: draft.contact_number.trim(),
        office_handphone: "",
        sort_order: 100,
      });
      setDraft({ contact_person: "", contact_number: "", department: "" });
      setOpen(true);          // keep the panel open so the new row is visible
      await onReload();
    } catch (ex) {
      await notify({
        title: "Couldn't add contact",
        message: formatErr(ex.response?.data?.detail) || "Please try again.",
        variant: "err",
      });
    }
    finally { setBusy(false); }
  };

  const removeContact = async (cid) => {
    try { await api.delete(`/emergency/contacts/${cid}`); await onReload(); }
    catch (ex) {
      await notify({
        title: "Couldn't remove contact",
        message: formatErr(ex.response?.data?.detail) || "Please try again.",
        variant: "err",
      });
    }
  };

  const startEditContact = (c) => {
    setEditContactId(c.id);
    setEditContactDraft({
      contact_person: c.contact_person || c.description_en || "",
      contact_number: c.contact_number || "",
      department: c.department || c.description_en || "",
      _original: c,
    });
  };

  const cancelEditContact = () => { setEditContactId(null); setEditContactDraft(null); };

  const saveEditContact = async () => {
    if (!editContactDraft) return;
    if (!editContactDraft.contact_number.trim()) {
      return notify({ title: "Phone required", message: "Contact number can't be empty.", variant: "err" });
    }
    setBusy(true);
    try {
      const orig = editContactDraft._original;
      await api.put(`/emergency/contacts/${editContactId}`, {
        scope: "site",
        site_id: site.id,
        category: orig.category || "onsite",
        description_en: editContactDraft.department.trim() || orig.description_en || "Emergency contact",
        description_zh: orig.description_zh || "",
        contact_person: editContactDraft.contact_person.trim(),
        department: editContactDraft.department.trim(),
        contact_number: editContactDraft.contact_number.trim(),
        office_handphone: orig.office_handphone || "",
        sort_order: orig.sort_order ?? 100,
      });
      cancelEditContact();
      await onReload();
    } catch (ex) {
      await notify({
        title: "Couldn't save contact",
        message: formatErr(ex.response?.data?.detail) || "Please try again.",
        variant: "err",
      });
    } finally { setBusy(false); }
  };

  return (
    <div className="vb-card p-5" data-testid={`wiz-site-row-${site.id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="vb-heading text-base m-0" style={{ color: "var(--vb-text-2)" }}>
            <MapPin size={14} className="inline -mt-0.5 mr-1 opacity-70" />
            {site.name}
          </div>
          {site.location && <div className="text-xs opacity-60 mt-0.5">{site.location}</div>}
          <div className="text-xs mt-1.5 opacity-70" data-testid={`wiz-site-${site.id}-contact-count`}>
            {contacts.length} emergency contact{contacts.length === 1 ? "" : "s"}
          </div>
        </div>
        <div className="flex gap-1.5">
          <button type="button" className="vb-btn ghost sm" onClick={() => setOpen(!open)}
            data-testid={`wiz-site-${site.id}-toggle`}>
            <Phone size={12} /> {open ? "Hide" : "Emergency contacts"}
          </button>
          <button type="button" className="vb-btn ghost sm" disabled={disabled}
            onClick={onEdit}
            data-testid={`wiz-site-edit-${site.id}`}>
            <Pencil size={12} />
          </button>
          <button type="button" className="vb-btn ghost sm" disabled={disabled}
            onClick={onDelete} style={{ color: "var(--vb-error)" }}
            data-testid={`wiz-site-${site.id}-delete`}>
            <Trash2 size={12} />
          </button>
        </div>
      </div>

      {open && (
        <div className="mt-4 pt-4 border-t space-y-3" style={{ borderColor: "var(--vb-border)" }}
          data-testid={`wiz-site-${site.id}-contacts`}>
          {err && <div className="vb-pill err w-full">{err}</div>}
          {contacts.map((c) => editContactId === c.id ? (
            // Inline edit mode — same compact 3-input layout as the add row.
            <div key={c.id} className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_1fr_auto] gap-2 items-end py-2 border-b last:border-0 rounded p-2"
              style={{ borderColor: "var(--vb-accent)", background: "var(--vb-bg-2)" }}
              data-testid={`wiz-contact-edit-${c.id}`}>
              <div>
                <Label>Name</Label>
                <input className="vb-input" value={editContactDraft.contact_person}
                  onChange={(e) => setEditContactDraft({ ...editContactDraft, contact_person: e.target.value })}
                  data-testid={`wiz-contact-edit-${c.id}-name`} />
              </div>
              <div>
                <Label>Role / dept</Label>
                <input className="vb-input" value={editContactDraft.department}
                  onChange={(e) => setEditContactDraft({ ...editContactDraft, department: e.target.value })}
                  data-testid={`wiz-contact-edit-${c.id}-role`} />
              </div>
              <div>
                <Label>Phone *</Label>
                <input className="vb-input" value={editContactDraft.contact_number}
                  onChange={(e) => setEditContactDraft({ ...editContactDraft, contact_number: e.target.value })}
                  data-testid={`wiz-contact-edit-${c.id}-phone`} />
              </div>
              <div className="flex gap-1">
                <button type="button" className="vb-btn sm" onClick={saveEditContact} disabled={busy}
                  data-testid={`wiz-contact-edit-${c.id}-save`}>Save</button>
                <button type="button" className="vb-btn ghost sm" onClick={cancelEditContact}
                  data-testid={`wiz-contact-edit-${c.id}-cancel`}>Cancel</button>
              </div>
            </div>
          ) : (
            <div key={c.id} className="flex items-center gap-3 text-sm py-1.5 border-b last:border-0"
              style={{ borderColor: "var(--vb-border)" }}
              data-testid={`wiz-contact-row-${c.id}`}>
              <Phone size={12} className="opacity-60 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="font-medium truncate">{c.contact_person || c.description_en}</div>
                <div className="text-xs opacity-70 truncate">
                  {c.department && `${c.department} · `}{c.contact_number}
                </div>
              </div>
              <button type="button" className="vb-btn ghost sm" onClick={() => startEditContact(c)}
                data-testid={`wiz-contact-${c.id}-edit`}>
                <Pencil size={11} />
              </button>
              <button type="button" className="vb-btn ghost sm" onClick={() => removeContact(c.id)}
                style={{ color: "var(--vb-error)" }}
                data-testid={`wiz-contact-${c.id}-delete`}>
                <Trash2 size={11} />
              </button>
            </div>
          ))}
          <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_1fr_auto] gap-2 items-end pt-2">
            <div>
              <Label>Name</Label>
              <input className="vb-input" value={draft.contact_person}
                onChange={(e) => setDraft({ ...draft, contact_person: e.target.value })}
                placeholder="e.g. Mr Tan"
                data-testid={`wiz-contact-${site.id}-name`} />
            </div>
            <div>
              <Label>Role / dept</Label>
              <input className="vb-input" value={draft.department}
                onChange={(e) => setDraft({ ...draft, department: e.target.value })}
                placeholder="e.g. Site Manager"
                data-testid={`wiz-contact-${site.id}-role`} />
            </div>
            <div>
              <Label>Phone *</Label>
              <input className="vb-input" value={draft.contact_number}
                onChange={(e) => setDraft({ ...draft, contact_number: e.target.value })}
                placeholder="+65 9123 4567"
                data-testid={`wiz-contact-${site.id}-phone`} />
            </div>
            <button type="button" className="vb-btn sm" onClick={addContact} disabled={busy}
              data-testid={`wiz-contact-${site.id}-add`}>
              <Plus size={12} /> Add
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function Label({ children }) {
  return (
    <label className="block text-[11px] font-semibold mb-1 uppercase tracking-wider" style={{ color: "var(--vb-text-2)" }}>
      {children}
    </label>
  );
}
